import { Fragment } from '@tiptap/pm/model';

const LIST_TYPES = new Set(['bulletList', 'orderedList']);
const BLOCK_TYPES_TO_FLATTEN = new Set([
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
  'table',
  'resourceTable',
]);

function paragraphRole(paragraph) {
  return paragraph?.attrs?.psQuoteRole === 'attribution' ? 'attribution' : 'quote';
}

function cleanParagraphAttrs(attrs, role) {
  return {
    ...attrs,
    psQuoteRole: role,
    postTextStyle: null,
    postTextAccent: null,
    psRowRole: null,
    indent: 0,
  };
}

/** Walk a block subtree and collect quote/attribution paragraph payloads. */
function collectParagraphSpecs(node, specs = []) {
  if (node.type.name === 'paragraph') {
    specs.push({
      role: paragraphRole(node),
      content: node.content,
    });
    return specs;
  }

  if (LIST_TYPES.has(node.type.name)) {
    node.forEach((listItem) => {
      listItem.forEach((child) => collectParagraphSpecs(child, specs));
    });
    return specs;
  }

  if (BLOCK_TYPES_TO_FLATTEN.has(node.type.name) || node.isBlock) {
    node.forEach((child) => collectParagraphSpecs(child, specs));
    return specs;
  }

  if (node.content?.size) {
    node.forEach((child) => collectParagraphSpecs(child, specs));
  }

  return specs;
}

/**
 * Normalize blockquote children to quote paragraphs plus at most one trailing attribution.
 */
export function buildNormalizedBlockquoteContent(blockquoteNode, schema) {
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) return null;

  const specs = [];
  blockquoteNode.forEach((child) => collectParagraphSpecs(child, specs));

  const quoteSpecs = [];
  const attributionSpecs = [];

  specs.forEach((spec) => {
    if (spec.role === 'attribution') attributionSpecs.push(spec);
    else quoteSpecs.push(spec);
  });

  if (quoteSpecs.length === 0 && attributionSpecs.length === 0) {
    return [paragraphType.create(cleanParagraphAttrs({}, 'quote'))];
  }

  const nodes = quoteSpecs.map((spec) =>
    paragraphType.create(cleanParagraphAttrs({}, 'quote'), spec.content)
  );

  if (attributionSpecs.length > 0) {
    const last = attributionSpecs[attributionSpecs.length - 1];
    nodes.push(
      paragraphType.create(cleanParagraphAttrs({}, 'attribution'), last.content)
    );
  }

  return nodes;
}

function blockquoteContentNeedsNormalization(blockquoteNode, normalized) {
  if (blockquoteNode.childCount !== normalized.length) return true;

  let needs = false;
  blockquoteNode.forEach((child, _offset, index) => {
    if (needs) return;
    const next = normalized[index];
    if (!next || child.type.name !== 'paragraph') {
      needs = true;
      return;
    }
    const expectedRole = next.attrs.psQuoteRole;
    if (
      child.attrs.psQuoteRole !== expectedRole ||
      child.attrs.postTextStyle != null ||
      child.attrs.postTextAccent != null ||
      child.attrs.psRowRole != null ||
      (child.attrs.indent && child.attrs.indent > 0)
    ) {
      needs = true;
    }
  });

  return needs;
}

/**
 * Flatten invalid nesting, strip paragraph styles, and enforce a single trailing attribution.
 * Returns true when `tr` was modified.
 */
export function sanitizePathstoneBlockquotes(tr, doc, schema) {
  const blockquoteType = schema.nodes.blockquote;
  const paragraphType = schema.nodes.paragraph;
  if (!blockquoteType || !paragraphType) return false;

  let modified = false;
  const blockquotes = [];

  doc.descendants((node, pos) => {
    if (node.type.name === 'blockquote') blockquotes.push({ node, pos });
  });

  for (let i = blockquotes.length - 1; i >= 0; i--) {
    const { node, pos } = blockquotes[i];
    const normalized = buildNormalizedBlockquoteContent(node, schema);
    if (!normalized) continue;

    if (!blockquoteContentNeedsNormalization(node, normalized)) continue;

    const from = pos + 1;
    const to = pos + node.nodeSize - 1;
    tr.replaceWith(from, to, Fragment.from(normalized));
    modified = true;
  }

  return modified;
}

/** True when the selection sits inside a Pathstone blockquote. */
export function selectionInsideBlockquote(state) {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === 'blockquote') return true;
  }
  return false;
}
